import { useCallback, useState, type ReactNode } from 'react';
import { Button, InlineNotification, ProgressBar } from '@carbon/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useDebouncedChange } from 'components/debounced-input';
import { Pagination } from 'components/pagination';
import { useDatasetConfig, useDatasetAtoms } from '../dataset-context';
import { mapSearchRefAtom, SUPPRESS_BOUNDS_SEARCH } from '../atoms';
import { accentColorForRow } from '../color-by-utils';
import { withMapQueryReferrer } from '../document-link';
import type { DocumentRow } from '../types';
import type { MapQueryExport } from '../use-map-query-export';
import { DocumentCard } from './document-card';
import { PreviewRail } from './preview-rail';
import { CardsToolbar } from './results-cards-toolbar';
import {
  useDatasetExport,
  useDatasetSearchChange,
  useRunsheetNavigation,
  useSelectHighlighted,
} from './hooks';
import type { Bounds, RowData } from './types';
import {
  PREVIEW_HOST_ID,
  cappedExpandAll,
  currentPaging,
  isDocumentRow,
} from './utils';
import styles from './results-section.module.scss';

const NO_ROWS: RowData[] = [];

/**
 * Highlights a card's map shape on select, and flies to it when the row carries
 * a representative point.
 * @return {Function} Selects a card's row.
 */
const useCardSelect = () => {
  const config = useDatasetConfig();
  const mapRef = useAtomValue(mapSearchRefAtom);
  const selectHighlighted = useSelectHighlighted();
  const rowIdKey = config.highlightRowKey ?? config.columns[0]?.accessorKey;
  return useCallback(
    (row: RowData) => {
      if (rowIdKey) {
        selectHighlighted(
          String(row[rowIdKey]),
          (row.mapBounds as Bounds | undefined) ?? null
        );
      }
      const point = config.flyToKey
        ? (row[config.flyToKey] as
            { coordinates?: [number, number] } | undefined)
        : undefined;
      if (mapRef && point?.coordinates) {
        const [lng, lat] = point.coordinates;
        // Tag the move so visible-extent mode doesn't rebound the bounded
        // search and drop the card we just selected.
        mapRef.flyTo(
          { center: [lng, lat], zoom: 12, speed: 1.5 },
          SUPPRESS_BOUNDS_SEARCH
        );
      }
    },
    [mapRef, selectHighlighted, config, rowIdKey]
  );
};

interface ResultsCardsProps {
  mapQueryExport: MapQueryExport;
  toolbarStart?: ReactNode;
}

/**
 * The runsheet layout: the document cards with their toolbar (search,
 * prev/next, expand/collapse all, export), the preview rail and pagination.
 * @param {ResultsCardsProps} props The runsheet.
 * @return {JSX.Element} The runsheet.
 */
export function ResultsCards({
  mapQueryExport,
  toolbarStart,
}: ResultsCardsProps) {
  const config = useDatasetConfig();
  const atoms = useDatasetAtoms();
  const search = useAtomValue(atoms.searchAtom);
  const { loading, currentPayload, error } = search;
  const showSearchError = Boolean(config.showSearchError && error);
  // A failed search keeps the previous results; they no longer match the
  // query, so none of them are shown.
  const data = showSearchError ? undefined : search.data;
  const setSearch = useSetAtom(atoms.searchAtom);
  const highlightId = useAtomValue(atoms.highlightedItemIdAtom);
  const colorRules = useAtomValue(atoms.colorRulesAtom);
  const datasetText = useAtomValue(atoms.datasetTextAtom);
  const rows = (data?.results as RowData[] | undefined) ?? NO_ROWS;
  const {
    cardScrollRef,
    activeCardIndex,
    activateCard,
    navTarget,
    previewId,
    setPreviewId,
    activePreviewId,
    previewRow,
    togglePreview,
    navigate,
    navAnchor,
  } = useRunsheetNavigation(rows);
  const { exportDataset, disabledReason, exporting } =
    useDatasetExport(mapQueryExport);
  const exportReason =
    disabledReason ?? (data?.totalCount ? undefined : 'No results to export');

  // The box echoes keystrokes locally and commits after the same 500 ms pause
  // as the page's global search box; outside text changes resync it.
  const handleSearchChange = useDatasetSearchChange();
  const [searchInput, changeSearchInput] = useDebouncedChange(
    datasetText,
    handleSearchChange
  );

  const handleCardSelect = useCardSelect();
  // A stable wrapper (not re-created per row) so DocumentCard's memo() can
  // actually skip re-rendering unaffected cards — a fresh per-row closure
  // here would defeat it.
  const handleDocumentCardSelect = useCallback(
    (document: DocumentRow) => {
      handleCardSelect({ ...document });
      // Selecting/expanding a card makes it the active one for prev/next.
      activateCard(document.id);
    },
    [handleCardSelect, activateCard]
  );
  const rowIdKey = config.highlightRowKey ?? config.columns[0]?.accessorKey;

  const documentLink = config.documentLink ?? withMapQueryReferrer;

  // ─── Expand/collapse all cards (broadcast to each card via a fresh object,
  //     mirroring the Global Interpretations runsheet) ───
  // The signal cascades into each card's sub-sections (Transaction Table / Title
  // Workspace), each lazy-fetching on open; cappedExpandAll caps at
  // EXPAND_ALL_LIMIT (= max page size) so expand-all opens the whole page.
  const [expandAll, setExpandAll] = useState<{ open: boolean } | undefined>();

  // The page's preview host, found once this section is in the document.
  const [previewHost, setPreviewHost] = useState<HTMLElement | null>(null);
  const findPreviewHost = useCallback((el: HTMLDivElement | null) => {
    if (el) setPreviewHost(document.getElementById(PREVIEW_HOST_ID));
  }, []);

  const { page, pageSize } = currentPaging(
    currentPayload,
    config.defaultPayload
  );

  return (
    <div className={styles.container} ref={findPreviewHost}>
      <CardsToolbar
        start={toolbarStart}
        label={config.label}
        searchInput={searchInput}
        onSearchInput={changeSearchInput}
        showSearchTips={!!config.showSearchTips}
        showSortBy={!!config.showSortBy}
        rowCount={rows.length}
        navAnchor={navAnchor}
        onNavigate={navigate}
        expandAll={expandAll}
        onExpandAll={setExpandAll}
        exportReason={exportReason}
        exporting={exporting}
        onExport={() => exportDataset()}
      />
      {loading && (
        <ProgressBar
          label="Loading"
          hideLabel
          size="small"
          className={styles.cardLoadingProgress}
        />
      )}
      <div className={styles.cardsAndViewer}>
        <div className={styles.cardScroll} ref={cardScrollRef}>
          {rows.map((row, index) =>
            isDocumentRow(row) ? (
              <DocumentCard
                key={String(row.id ?? (rowIdKey ? row[rowIdKey] : index))}
                document={row}
                selected={!!rowIdKey && highlightId === String(row[rowIdKey])}
                active={index === activeCardIndex}
                accentColor={accentColorForRow(
                  row,
                  colorRules,
                  config.colorByAttributes ?? []
                )}
                onSelect={handleDocumentCardSelect}
                expandAll={cappedExpandAll(expandAll, index)}
                navOpen={
                  navTarget?.index === index ? navTarget.open : undefined
                }
                onPreview={togglePreview}
                previewing={previewId === Number(row.id)}
                documentLink={documentLink}
              />
            ) : null
          )}
          {showSearchError && (
            <div className={styles.cardError}>
              <InlineNotification
                kind="error"
                lowContrast
                hideCloseButton
                title="Couldn't load this entity's documents"
                subtitle={error}
              />
              <Button
                kind="tertiary"
                size="sm"
                onClick={() => setSearch((current) => current)}
              >
                Retry
              </Button>
            </div>
          )}
          {!loading && !showSearchError && rows.length === 0 && (
            <div className={styles.cardEmpty}>No results to display</div>
          )}
        </div>
      </div>
      <PreviewRail
        host={previewHost}
        documentId={activePreviewId}
        open={previewId !== undefined}
        row={previewRow}
        onPrev={() => navigate(-1)}
        onNext={() => navigate(1)}
        hasPrev={navAnchor > 0}
        hasNext={navAnchor >= 0 && navAnchor < rows.length - 1}
        onClose={() => setPreviewId(undefined)}
      />
      <Pagination
        page={page}
        pageSize={pageSize}
        pageSizes={[25, 50, 100]}
        totalItems={data?.totalCount ?? 0}
        onChange={(next) => {
          // Drop the expand-all broadcast so a new page loads collapsed
          // instead of re-auto-expanding (and re-fetching) its first N cards.
          setExpandAll(undefined);
          setSearch((current) => ({
            ...(current ?? config.defaultPayload),
            page: next.page,
            pageSize: next.pageSize,
          }));
        }}
      />
    </div>
  );
}
